import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { useProjectCustomFields } from '@/hooks/useProjectCustomFields';
import ChecklistTaskFieldset from '@/ui/components/createEditTaskSidebar/components/ChecklistTaskFieldset';
import CustomFieldTaskFieldset from '@/ui/components/createEditTaskSidebar/components/CustomFieldTaskFieldset';
import FormTaskFieldset from '@/ui/components/createEditTaskSidebar/components/FormTaskFieldset';
import {
  CreateEditTaskFormValues,
  TASK_SCHEMA,
} from '@/ui/components/createEditTaskSidebar/types';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Conditional,
  DateInput,
  Divider,
  FIELDSET_CLASSNAME,
  Field,
  Fieldset,
  Form,
  RadioGroupField,
  RawInput,
  Select,
  Textarea,
} from '@payaca/ambrosia';

import TextareaFieldMentionFormatter from '@payaca/components/textareaField/TextareaFieldMentionFormatter';
import AppearTransition from '@payaca/components/transitions/AppearTransition';
import { useFirstMountState, useScroll } from '@payaca/hooks';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { clstx } from '@payaca/utilities/cvaMerge';
import { isPast } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

const CreateEditTaskSidebarControl: FC<{
  formType?: 'task' | 'task-template' | 'task-template-with-identifier';
  onSubmit: (
    data: CreateEditTaskFormValues,
    originalData: CreateEditTaskFormValues
  ) => void;
  isProcessing: boolean;
  isEditing: boolean;
  defaultValues: CreateEditTaskFormValues;
  setIsDirty: (isDirty: boolean) => void;
  canCreateCustomFieldTask: boolean;
  canCreateFormTask: boolean;
}> = (props) => {
  const {
    formType = 'task',
    onSubmit,
    isProcessing,
    defaultValues,
    isEditing,
    setIsDirty,
    canCreateCustomFieldTask,
    canCreateFormTask,
  } = props;

  const { projectCustomFields } = useProjectCustomFields(['fieldset']);

  /**
   * state
   */
  const isFirstMount = useFirstMountState();
  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
  useScroll({ current: scrollRef });

  /**
   * queries
   */
  const { data: accountUsers } = useGetMyAccountUsersExcludedDeactivated();
  const assigneeOptions = accountUsers.map((user) => ({
    label: user.fullName,
    value: user.publicId,
  }));
  assigneeOptions.push({
    label: 'Unassigned',
    // @ts-expect-error
    value: null,
  });

  /**
   * react hook form
   */
  const formMethods = useForm<CreateEditTaskFormValues>({
    defaultValues,
    resolver: zodResolver(TASK_SCHEMA),
  });

  const {
    control,
    watch,
    formState: { isDirty },
  } = formMethods;

  const type = watch('type');
  const deadlineDate = watch('dueDate');

  /**
   * effects
   */
  useEffect(() => {
    setIsDirty(isDirty);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'setIsDirty'
  }, [isDirty]);

  const taskTypeOptions = [{ label: 'Checklist', value: 'checklist' }];
  if (canCreateFormTask) {
    taskTypeOptions.push({ label: 'Form - PDF', value: 'form' });
  }
  if (canCreateCustomFieldTask && projectCustomFields.length > 0) {
    taskTypeOptions.push({
      label: 'Form - Responsive',
      value: 'custom-fields',
    });
  }

  const isDeadlineInThePast = !!(deadlineDate && isPast(deadlineDate));

  const hasScrollShadow = !(
    scrollRef &&
    scrollRef.scrollHeight - scrollRef.scrollTop === scrollRef.clientHeight
  );

  return (
    <div className="overflow-auto h-full" ref={setScrollRef}>
      <Form
        className="h-full flex flex-col"
        methods={formMethods}
        onSubmit={(data) => onSubmit(data, defaultValues)}
        onError={console.error}
      >
        <Fieldset className="p-4">
          {formType === 'task-template-with-identifier' && (
            <>
              <Field name="templateName">
                <Field.Label>Template name</Field.Label>

                <RawInput {...formMethods.register('templateName')} />
              </Field>

              <Controller
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <Field name={name}>
                      <Field.Label>Template description</Field.Label>

                      <Textarea value={value} onChange={onChange} />
                    </Field>
                  );
                }}
                name="templateDescription"
              />

              <Divider />
            </>
          )}

          {!isEditing && (
            <Controller
              render={({ field: { onChange, value, name } }) => {
                return (
                  <Field name={name}>
                    <Field.Label>Task type</Field.Label>

                    <RadioGroupField
                      value={value}
                      onChange={(type) => onChange(type)}
                      options={taskTypeOptions}
                    />
                  </Field>
                );
              }}
              name="type"
              control={control}
            />
          )}

          {type === 'checklist' && (
            <AppearTransition
              appear={!isFirstMount}
              className={FIELDSET_CLASSNAME}
            >
              <ChecklistTaskFieldset />
            </AppearTransition>
          )}

          {type === 'form' && (
            <AppearTransition
              appear={!isFirstMount}
              className={FIELDSET_CLASSNAME}
            >
              <FormTaskFieldset isEditing={isEditing} />
            </AppearTransition>
          )}

          {type === 'custom-fields' && (
            <AppearTransition
              appear={!isFirstMount}
              className={FIELDSET_CLASSNAME}
            >
              <CustomFieldTaskFieldset isEditing={isEditing} />
            </AppearTransition>
          )}

          {formType === 'task' && (
            <>
              <PermissionGuard
                renderIfHasPermissions={[AccountsPermissions.GET_USERS]}
              >
                <Controller
                  name="assignedTo"
                  control={control}
                  shouldUnregister
                  render={({ field: { value, onChange, name } }) => {
                    return (
                      <Field name={name}>
                        <Field.Label>Assignee</Field.Label>

                        <Select
                          multiple={false}
                          value={value ?? undefined}
                          onChange={onChange}
                          options={assigneeOptions}
                        />
                      </Field>
                    );
                  }}
                />
              </PermissionGuard>

              <Controller
                name="dueDate"
                control={control}
                shouldUnregister
                render={({ field: { value, onChange, name } }) => {
                  return (
                    <Field name={name}>
                      <Field.Label>Deadline date</Field.Label>

                      <DateInput value={value} onChange={onChange} />
                    </Field>
                  );
                }}
              />
            </>
          )}

          <Controller
            name="notes"
            control={control}
            shouldUnregister
            render={({ field: { value, onChange, name } }) => {
              return (
                <Field name={name}>
                  <Field.Label>Task notes</Field.Label>

                  <TextareaFieldMentionFormatter
                    name={name}
                    onChange={({ notes }) => {
                      onChange(notes);
                    }}
                    value={value}
                    className="[&_.ql-editor]:!min-h-[100px]"
                  />
                </Field>
              );
            }}
          />
        </Fieldset>

        <div
          className={clstx(
            'mt-auto sticky bottom-0 p-4 bg-white',
            hasScrollShadow && 'shadow-flipped-lg'
          )}
        >
          <Conditional condition={isDeadlineInThePast && !isEditing}>
            <Alert className="mb-4" colour="yellow">
              Deadline date is in the past.
            </Alert>
          </Conditional>

          <Button
            className="w-full"
            type="submit"
            isProcessing={isProcessing}
            disabled={(isEditing && !isDirty) || isProcessing}
          >
            {isEditing ? 'Update' : 'Create'} task
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateEditTaskSidebarControl;
